'use client';

import { User } from 'next-auth';
import { signOut } from 'next-auth/react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { UserAvatar } from '@/components/user-avatar';
import { SidebarNavItem } from '@/types';

interface UserAccountNavProps extends React.HTMLAttributes<HTMLDivElement> {
  user: Pick<User, 'name' | 'image' | 'email'>;
}

const navItems: SidebarNavItem[] = [
  {
    title: 'Billing',
    href: '/instructor/dashboard/billing',
    icon: 'billing'
  },
  {
    title: 'Settings',
    href: '/settings/account',
    icon: 'settings'
  }
];

export function UserAccountNav({ user }: UserAccountNavProps) {
  const pathname = usePathname();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="rounded-full">
        <UserAvatar
          user={{ name: user.name || null, image: user.image || null }}
          className="h-8 w-8"
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <div className="flex items-center justify-start gap-2 p-2">
          <div className="flex flex-col space-y-1 leading-none">
            {user.name && <p className="font-medium">{user.name}</p>}
            {user.email && (
              <p className="w-[200px] truncate text-sm text-muted-foreground">
                {user.email}
              </p>
            )}
          </div>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          {pathname.includes('/instructor') ? (
            <Link href="/dashboard">Switch to User</Link>
          ) : (
            <Link href="/instructor/dashboard">Switch to Admin</Link>
          )}
        </DropdownMenuItem>
        {navItems.map((item) => (
          <DropdownMenuItem asChild key={item.title}>
            <Link href={item.href || '#'}>{item.title}</Link>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="cursor-pointer"
          onSelect={(event) => {
            event.preventDefault();
            signOut();
          }}
        >
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
